import { fileUrl, formatDate } from '@tnn/core'
import type { Signature } from '@tnn/domain'
import { currentLocale } from '@tnn/i18n'
import { createContext, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import classes from './mark.module.css'

/** Where a signature's image is: `/api/files` once signed in; a public page (a link) gives its own */
const SignatureFiles = createContext<(fileId: string) => string>(fileUrl)
export const SignatureFilesProvider = SignatureFiles.Provider
/** A signature's image's address, where this page has them (`SignatureFilesProvider`) */
export const useSignatureFile = () => useContext(SignatureFiles)

/** A signature as a printed line shows it; one signed on paper (`method: paper`) prints no image, only that it was */
export type PrintedSignature = Pick<Signature, 'fileId' | 'name' | 'title' | 'signedAt'> & Partial<Pick<Signature, 'method'>>

/** Who a line is for and when, printed with no ink on it: the cashier under a receipt's ผู้รับเงิน */
export interface PrintedName {
  name: string
  at: string
}

/**
 * A signature line on a printed document, as Thai forms have it: the signature on the line, (the name), the title,
 * who signs there, the date — or `named`, a name and date printed with no signature, or a blank line and date to sign
 * by hand
 */
export function SignatureMark({
  label,
  signature,
  named,
  dated = true,
}: {
  label: string
  signature?: PrintedSignature | null
  named?: PrintedName | null
  dated?: boolean
}) {
  const { t, i18n } = useTranslation()
  const url = useContext(SignatureFiles)
  const who = signature ? { name: signature.name, at: signature.signedAt } : named
  return (
    <div className={classes.mark}>
      <div className={classes.ink}>
        {signature &&
          (signature.method === 'paper' ? (
            <span className={classes.caption}>{t('signing.onPaper')}</span>
          ) : (
            <img src={url(signature.fileId)} alt={t('signature.alt')} />
          ))}
      </div>
      <div className={classes.line} />
      {who && <div>({who.name})</div>}
      {signature?.title && <div className={classes.caption}>{signature.title}</div>}
      <div>{label}</div>
      {dated && (
        <div className={classes.caption}>
          {t('signature.date')} {who ? formatDate(who.at, { locale: currentLocale(i18n), style: 'long' }) : '____/____/________'}
        </div>
      )}
    </div>
  )
}

/** The signature on `role`'s line, if signed */
export const onLine = (signatures: readonly Signature[] | undefined, role: string) => signatures?.find((s) => s.role === role) ?? null
